import React, {useState, useRef, useEffect, useCallback} from 'react';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {Helmet} from 'react-helmet';
import {useContext, useContextSelector} from 'use-context-selector';
import {SessionContext} from '../context/SessionContext';
import {Button, Form, Alert, Modal} from 'react-bootstrap';
import {LinkContainer} from 'react-router-bootstrap';
import {useTranslation} from 'react-i18next';
import ModalRef from '../util/ModalRef';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSyncAlt} from '@fortawesome/free-solid-svg-icons/faSyncAlt';

import {LocalContext} from '../context/LocalContext';
import useOnFieldChange from '../util/useOnFieldChange';

import Layout from '../common/Layout';
import LayoutContentContainer from '../common/LayoutContentContainer';
import FieldContainer from '../common/FieldContainer';
import useSubmitContactMessage from './useSubmitContactMessage';
import useCreateClientStatusMessage from '../util/useCreateClientStatusMessage';

const Contact = () => {
    const {executeRecaptcha} = useGoogleReCaptcha();
    const {t} = useTranslation();

    const isMounted = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, []);

    const [sessionData, setSessionData] = useContext(SessionContext);
    const modalVisible = useContextSelector(SessionContext, v => v[0].modalVisible);

    const clientOnline = useContextSelector(LocalContext, v => v[0].clientOnline);

    const UILang = useContextSelector(LocalContext, v => v[0].UILang);

    const createClientStatusMessage = useCreateClientStatusMessage();
    const submitContactMessage = useSubmitContactMessage();

    const fieldsContactInitial = {
        valid: false,
        fields: {
            subject: {
                value: '',
                validate: true,
                rules: [
                    {
                        type: 'validOnEmpty'
                    },
                    {
                        type: 'maxLength',
                        length: 255
                    },
                ],
                required: false,
                valid: true,
                touched: false,
                errors: [],
                errorsFragment: []
            },
            messageBody: {
                value: '',
                validate: true,
                rules: [
                    {
                        type: 'notEmpty'
                    }
                ],
                required: true,
                valid: false,
                touched: false,
                errors: [],
                errorsFragment: []
            },
            email: {
                value: (''),
                validate: true,
                rules: [
                    {
                        type: 'notEmpty'
                    },
                    {
                        type: 'isEmail'
                    }
                ],
                required: true,
                valid: false,
                touched: false,
                errors: [],
                errorsFragment: []
            }
        }
    }

    const [fieldsContact, setFieldsContact] = useState({...fieldsContactInitial});

    const onFieldContactChange = useOnFieldChange(fieldsContact, setFieldsContact);

    const [isLoadingSubmitContactMessage, setIsLoadingSubmitContactMessage] = useState(false);
    const [isLoadedSubmitContactMessage, setIsLoadedSubmitContactMessage] = useState(false);
    const [isErrorSubmitContactMessage, setIsErrorSubmitContactMessage] = useState(false);

    const handleSubmitContactMessage = useCallback(
        async () => {
            let recaptchaToken
            try {
                recaptchaToken = await executeRecaptcha();
            } catch (error) {
                console.log(error);
                setIsLoadingSubmitContactMessage(false);
                setIsErrorSubmitContactMessage(true);
                return
            }

            setIsLoadingSubmitContactMessage(true);
            let response;

            try {
                response = await submitContactMessage(fieldsContact.fields, recaptchaToken);
            } catch (error) {
                if (isMounted.current) {
                    setIsLoadingSubmitContactMessage(false);
                    setIsErrorSubmitContactMessage(true);
                    createClientStatusMessage(error.clientStatusMessage);
                }
                return
            }

            if (isMounted.current) {
                setIsLoadingSubmitContactMessage(false);
                setIsLoadedSubmitContactMessage(true);
            }
        },
        [
            executeRecaptcha,
            fieldsContact,
            setIsLoadingSubmitContactMessage,
            submitContactMessage,
            setIsLoadedSubmitContactMessage,
            setIsErrorSubmitContactMessage,
            createClientStatusMessage,
            isMounted
        ]
    );

    return <Layout navSecondaryShow={true} className="bg-light" contentBackgroundArt={true}>
        {
            isLoadedSubmitContactMessage && !isErrorSubmitContactMessage &&
            <Modal
                container={ModalRef}
                show={true}
                onHide={
                    () => {
                        if (modalVisible) {
                            setSessionData({
                                ...sessionData,
                                modalVisible: false,
                            });
                        }
                    }
                }
                onShow={
                    () => {
                        if (!modalVisible) {
                            setSessionData({
                                ...sessionData,
                                modalVisible: true,
                            });
                        }
                    }
                }
                centered={true}
            >
                <Modal.Body>
                    <Alert
                        className="mb-0"
                        variant="info"
                    >
                        <p>Message has been sent successfully.</p>
                        <p className="mb-0">Please wait for a response in <em>{fieldsContact.fields.email.value}</em> inbox.</p>
                    </Alert>
                </Modal.Body>
                <Modal.Footer
                    className="bg-light"
                >
                    <LinkContainer to="/" exact={true}>
                        <Button
                            onClick={() => {
                                if (modalVisible) {
                                    setSessionData(
                                        {
                                            ...sessionData,
                                            modalVisible: false
                                        }
                                    )
                                }
                            }}
                            variant="primary">{t('Contact.go-to-home')}</Button>
                    </LinkContainer>
                </Modal.Footer>
            </Modal>
        }

        {
            isErrorSubmitContactMessage &&
            <Modal
                container={ModalRef}
                show={true}
                onHide={
                    () => {
                        if (modalVisible) {
                            setSessionData({
                                ...sessionData,
                                modalVisible: false,
                            });
                        }
                    }
                }
                onShow={
                    () => {
                        if (!modalVisible) {
                            setSessionData({
                                ...sessionData,
                                modalVisible: true,
                            });
                        }
                    }
                }
                centered={true}
            >
                <Modal.Body>
                    <Alert
                        className="mb-0"
                        variant="warning"
                    >
                        <p>Could not send the message due to a technical problem.</p>
                    </Alert>
                </Modal.Body>
                <Modal.Footer
                    className="bg-light"
                >
                    <Button variant="primary" onClick={() => {
                        setIsErrorSubmitContactMessage(false);
                        if (modalVisible) {
                            setSessionData(
                                {
                                    ...sessionData,
                                    modalVisible: false
                                }
                            )
                        }
                    }}>Close</Button>
                </Modal.Footer>
            </Modal>
        }
        <Helmet><title>{t('Contact.page-head-title')}</title></Helmet>
        <LayoutContentContainer
            style={
                {
                    maxWidth: '50rem'
                }
            }
        >
            <h1
                style={
                    {
                        fontWeight: 400
                    }
                }
            >{t('Contact.contact-us')}</h1>
            <Form>
                <FieldContainer
                    noSeparator={true}
                    dense={true}
                    label={
                        <Form.Label htmlFor="contact-subject">{t('Contact.subject')} {fieldsContact.fields.subject.required && <span className="form-field-container__required-field-indicator">*</span>}</Form.Label>
                    }
                    control={
                        <>
                            <Form.Control
                                autoFocus={true}
                                id="contact-subject"
                                // placeholder={t('Contact.subject')}
                                isInvalid={!fieldsContact.fields.subject.valid && fieldsContact.fields.subject.touched}
                                disabled={isLoadingSubmitContactMessage}

                                isValid={fieldsContact.fields.subject.valid && fieldsContact.fields.subject.touched}
                                onChange={(event) => {
                                    onFieldContactChange(event.target.value, 'subject');
                                }}
                            />
                            <Form.Control.Feedback
                                type="invalid"
                            >
                                <div>{fieldsContact.fields.subject.errorsFragment}</div>
                            </Form.Control.Feedback>
                        </>
                    }
                />
                <FieldContainer
                    noSeparator={true}
                    dense={true}
                    label={
                        <Form.Label htmlFor="contact-message-body">{t('Contact.message-body')} {fieldsContact.fields.messageBody.required && <span className="form-field-container__required-field-indicator">*</span>}</Form.Label>
                    }
                    control={
                        <>
                            <Form.Control
                                id="contact-message-body"
                                as="textarea"
                                rows="5"
                                disabled={isLoadingSubmitContactMessage}
                                // placeholder={t('Contact.message-body')}
                                isInvalid={!fieldsContact.fields.messageBody.valid && fieldsContact.fields.messageBody.touched}

                                isValid={fieldsContact.fields.messageBody.valid && fieldsContact.fields.messageBody.touched}
                                onChange={(event) => {
                                    onFieldContactChange(event.target.value, 'messageBody');
                                }}
                            />
                            <Form.Control.Feedback
                                type="invalid"
                            >
                                <div>{fieldsContact.fields.messageBody.errorsFragment}</div>
                            </Form.Control.Feedback>
                        </>
                    }
                />
                <FieldContainer
                    noSeparator={true}
                    dense={true}
                    label={
                        <Form.Label htmlFor="contact-email-address">{t('Contact.email-address')} {fieldsContact.fields.email.required && <span className="form-field-container__required-field-indicator">*</span>}</Form.Label>
                    }
                    control={
                        <>
                            <Form.Control
                                id="contact-email-address"
                                value={fieldsContact.fields.email.value}
                                disabled={isLoadingSubmitContactMessage}
                                // placeholder={t('Contact.email-address')}
                                isInvalid={!fieldsContact.fields.email.valid && fieldsContact.fields.email.touched}

                                isValid={fieldsContact.fields.email.valid}
                                onChange={(event) => {
                                    onFieldContactChange(event.target.value, 'email');
                                }}
                            />
                            <Form.Control.Feedback
                                type="invalid"
                            >
                                <div>{fieldsContact.fields.email.errorsFragment}</div>
                            </Form.Control.Feedback>
                        </>
                    }
                />
                <p
                    style={
                        {
                            fontSize: '0.85rem',
                            marginTop: '2rem'
                        }
                    }
                >{t('message-conditions-recaptcha.p1')}<a rel="noopener noreferrer" target="_blank" href="https://policies.google.com/privacy">{t('message-conditions-recaptcha.p2')}</a>{t('message-conditions-recaptcha.p3')}<a rel="noopener noreferrer" target="_blank" href="https://policies.google.com/terms">{t('message-conditions-recaptcha.p4')}</a> {t('message-conditions-recaptcha.p5')}</p>

                <div
                    style={
                        {
                            display: 'flex',
                            marginTop: '2rem'
                        }
                    }
                >
                    <Button
                        className="ml-auto"
                        variant="primary"
                        disabled={!fieldsContact.valid || !clientOnline || isLoadingSubmitContactMessage}
                        onClick={
                            async (event) => {
                                event.preventDefault();
                                await handleSubmitContactMessage();
                            }
                        }
                        type="submit"
                    >
                        {isLoadingSubmitContactMessage ? <span className="mr-2">
                            <FontAwesomeIcon
                                spin={isLoadingSubmitContactMessage}
                                size="sm"
                                icon={faSyncAlt}
                            />
                        </span> : null}
                        {t('Contact.send-message')}</Button>
                </div>
            </Form>
        </LayoutContentContainer>
    </Layout >
}

export default Contact;
